import {
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  User as FirebaseUser
} from 'firebase/auth';
import { auth } from './config';
import { AuthUser } from '../types';
import { getUserIP } from '../utils';
import { checkIPConflict, updateUserIP, getOrCreateUser } from './firestore';

// Admin detection helper function
const isAdmin = (email: string | null): boolean => {
  if (!email) return false;
  const adminEmails = [
    'admin@mecon.com',
    'muhasebe@meconyapi.com',
    // Add more admin emails as needed
  ];
  return adminEmails.includes(email.toLowerCase());
};

export const signIn = async (email: string, password: string) => {
  try {
    // Get user's IP address
    const ipAddress = await getUserIP();
    
    // Sign in with Firebase
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Get or create user document
    const userDoc = await getOrCreateUser(user);
    
    // Check if user is disabled
    if (userDoc.disabled) {
      // User is disabled - sign them out and show error
      await firebaseSignOut(auth);
      throw new Error('Hesabınız devre dışı bırakılmıştır. Lütfen yöneticinizle iletişime geçin.');
    }
    
    // Skip IP conflict check and IP tracking for admins
    if (!isAdmin(user.email)) {
      // Check for IP conflicts with other users (only for non-admin users)
      const hasConflict = await checkIPConflict(user.uid, ipAddress);
      
      if (hasConflict) {
        // IP conflict found - show alert and sign out
        alert('Bu cihazda başka bir hesap kullanıldığı için giriş yapılamıyor. Aynı cihazdan birden fazla hesapla giriş yapılamaz. Bunun bir hata olduğunu düşünüyorsanız destek ekibimizle iletişime geçin.');
        await firebaseSignOut(auth);
        throw new Error('This IP address is already being used by another account. Please try again later or contact support.');
      }
      
      // Update user's IP information (only for non-admin users)
      await updateUserIP(user.uid, ipAddress);
    }
    
    return user;
  } catch (error) {
    throw error;
  }
};

export const signOutUser = async () => {
  try {
    await firebaseSignOut(auth);
  } catch (error) {
    throw error;
  }
};

export const onAuthChange = (callback: (user: AuthUser | null) => void) => {
  return onAuthStateChanged(auth, (user: FirebaseUser | null) => {
    if (user) {
      const authUser: AuthUser = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName
      };
      callback(authUser);
    } else {
      callback(null);
    }
  });
};

export const createUser = async (email: string, password: string): Promise<FirebaseUser> => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error: any) {
    console.error('Error creating user:', error);
    throw new Error(error.message);
  }
};

export const signInUser = async (email: string, password: string) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error) {
    throw error;
  }
};

export const createUserWithoutSignIn = async (email: string, password: string) => {
  try {
    const currentUser = auth.currentUser;
    
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const newUser = userCredential.user;
    
    await signOutUser();
    
    if (currentUser) {
      // Note: This would require storing the admin's password, which is not secure
      // For now, we'll just return the new user's UID and let the admin handle re-authentication
    }
    
    return newUser.uid;
  } catch (error) {
    throw error;
  }
}; 